<template>
  <div class="user-create-modal bg-white modal-card">
    <header class="user-create-modal__header">
      <div>
        <h2>Ajouter des utilisateurs</h2>
        <p>Créez un ou plusieurs comptes et définissez leur niveau d’accès individuellement.</p>
      </div>
      <button type="button" aria-label="Fermer" :disabled="loading" @click="$parent.close()">
        <i class="fas fa-times" />
      </button>
    </header>

    <fieldset class="user-create-modal__content" :disabled="loading" :aria-busy="loading ? 'true' : 'false'">
      <div class="user-create-modal__forms">
        <Form-create
          v-for="(user, i) of users"
          :key="`user-${i}`"
          :user="user"
          :userIndex="i"
          :isLast="i === users.length - 1"
          :isFirst="i === 0"
          :groups="groups"
          :roleOptions="roleOptions"
          @update="({ label, value }) => users[i][label] = value"
          @addUser="addUser"
          @removeUser="removeUser(i)"
          @error="$event => errors[i] = $event"
        />
      </div>
    </fieldset>

    <footer class="user-create-modal__footer">
      <button
        type="button"
        class="user-create-modal__cancel"
        :disabled="loading"
        @click="$parent.close()"
      >
        Annuler
      </button>
      <button
        type="button"
        class="user-create-modal__submit"
        :disabled="loading"
        @click="create"
      >
        <i v-if="loading" class="fas fa-spinner fa-spin mr-2" />
        Créer {{ users.length > 1 ? `${users.length} comptes` : 'le compte' }}
      </button>
    </footer>
  </div>
</template>

<script>
import { http } from '@/services/api.service'
import FormCreate from '../FormCreate.vue'

export default {
  name: 'UserCreateModal',
  components: { FormCreate },
  props: {
    roleOptions: {
      type: Array,
      default: () => [{ value: 'user', label: 'Utilisateur' }]
    }
  },
  data () {
    return {
      loading: false,
      users: [{
        email: '',
        internal_id_number: '',
        firstname: '',
        lastname: '',
        role: this.roleOptions[0]?.value || 'user',
        groupsSelected: []
      }],
      groups: [],
      errors: [false]
    }
  },
  created () {
    this.loadGroups()
  },
  methods: {
    async create () {
      if (this.loading || this.errors.some(error => error === true)) {
        this.$buefy.notification.open({
          duration: 3000,
          message: 'Veuillez corriger les erreurs du formulaire avant de créer les utilisateurs.',
          position: 'is-bottom-left',
          type: 'is-warning',
          hasIcon: true
        })
        return
      }

      this.loading = true
      const failedUsers = []
      for (const user of this.users) {
        try {
          const payload = {
            ...user,
            groups: user.groupsSelected || []
          }
          const res = await http.post('/user/management', payload)
          this.$emit('created', res.data)
        } catch (err) {
          failedUsers.push(user)
          this.$buefy.notification.open({
            duration: 3000,
            message: err.response?.data?.message || 'Erreur lors de la création du compte.',
            position: 'is-bottom-left',
            type: 'is-warning',
            hasIcon: true,
            icon: 'server'
          })
        }
      }
      this.loading = false
      if (!failedUsers.length) {
        this.$parent.close()
        return
      }

      this.users = failedUsers
      this.errors = failedUsers.map(() => false)
    },
    addUser () {
      this.users.push({
        email: null,
        internal_id_number: null,
        firstname: null,
        lastname: null,
        role: this.roleOptions[0]?.value || 'user',
        groupsSelected: []
      })
      this.errors.push(false)
    },
    removeUser (index) {
      this.users.splice(index, 1)
      this.errors.splice(index, 1)
    },
    loadGroups () {
      http.get('/groups').then((res) => {
        this.groups = res.data
      })
    }
  }
}
</script>

<style scoped>
.user-create-modal {
  width: min(52rem, calc(100vw - 2rem));
  max-height: calc(100vh - 3rem);
  overflow: hidden;
  border-radius: 1rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.2);
}

.user-create-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.user-create-modal__header h2 {
  color: #111827;
  font-size: 1.25rem;
  font-weight: 700;
}

.user-create-modal__header p {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.875rem;
}

.user-create-modal__header button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 1px solid #d1d5db;
  border-radius: 9999px;
  color: #64748b;
}

.user-create-modal__header button:hover:not(:disabled) {
  background: #f8fafc;
  color: #111827;
}

.user-create-modal__header button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.user-create-modal__content {
  max-height: calc(100vh - 12.5rem);
  overflow-y: auto;
}

.user-create-modal__content:disabled {
  opacity: 0.7;
}

.user-create-modal__forms {
  padding: 1.5rem;
}

.user-create-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

.user-create-modal__cancel,
.user-create-modal__submit {
  min-height: 42px;
  padding: 0.65rem 1rem;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 700;
}

.user-create-modal__cancel {
  border: 1px solid #d1d5db;
  background: #fff;
  color: #475569;
}

.user-create-modal__cancel:hover:not(:disabled) {
  background: #f1f5f9;
}

.user-create-modal__submit {
  background: #2563eb;
  color: #fff;
}

.user-create-modal__submit:hover:not(:disabled) {
  background: #1d4ed8;
}

.user-create-modal__cancel:disabled,
.user-create-modal__submit:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.user-create-modal__header button:focus-visible,
.user-create-modal__cancel:focus-visible,
.user-create-modal__submit:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

</style>
